import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiArrowRight, FiArrowLeft, FiInfo, FiExternalLink, FiCalendar, FiCopy, FiX, FiZap } = FiIcons;

const PACKAGES = {
  starter: { name: 'Starter Visibility', price: 600, relief: 'Perfect for maintaining a professional presence while you stay in the field.' },
  consistent: { name: 'Consistent & Credible', price: 900, isPopular: true, relief: 'The "sweet spot" for pet businesses ready to dominate their local market.' },
  leader: { name: 'Local Leader', price: 1200, relief: 'Total market authority. You become the go-to name for pet care in your area.' }
};

const ADD_ONS_DATA = [
  { id: 'brandAds', name: 'Brand Awareness Ads Management', price: 450, explainer: 'Get seen by more local pet owners. We promote your best content to increase reach.', note: 'Ad spend is paid to Meta/TikTok, not us.', spend: 'Recommended: $5–$20/day' },
  { id: 'leadAds', name: 'Lead Generation Ads Management', price: 850, explainer: 'Turn attention into inquiries. We run conversion-focused campaigns for bookings.', note: 'Ad spend is paid to Meta/TikTok, not us.', spend: 'Recommended: $50–$200/day' },
  { id: 'contentDay', name: 'Quarterly Content Capture Day', price: 0, explainer: 'Quarterly photo + video capture so you always have fresh, high-quality content.', note: 'Quoted based on location + scope.', isCustomQuote: true }
];

export default function EstimateCalculator({ isModal = false, onClose }) {
  const [step, setStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    businessType: '',
    location: '',
    goal: '',
    platforms: '',
    brandAds: false,
    leadAds: false,
    contentDay: false
  });
  const [result, setResult] = useState(null);

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  const calculateRecommendation = () => {
    let pkg = PACKAGES.consistent;
    let note = '';
    if (formData.goal === 'Consistency/Trust') pkg = PACKAGES.consistent;
    else if (formData.goal === 'Visibility') pkg = PACKAGES.consistent;
    else if (formData.goal === 'Leads/Bookings') pkg = PACKAGES.leader;

    const selectedAddOns = ADD_ONS_DATA.filter(addon => formData[addon.id]);
    setResult({ pkg, selectedAddOns, note });
    nextStep();
  };

  const toggleAddon = (id) => {
    setFormData(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopy = () => {
    const total = result.pkg.price + result.selectedAddOns.reduce((acc, curr) => acc + (curr.price || 0), 0);
    const addonsText = result.selectedAddOns.map(a => `- ${a.name}: ${a.isCustomQuote ? 'Custom Quote' : `$${a.price}/mo`}`).join('\n');
    const summary = `Otso Co. Estimate Summary\n\nPackage: ${result.pkg.name} ($${result.pkg.price}/mo)\nAdd-ons:\n${addonsText || 'None'}\n\nEstimated Total: $${total}/mo\n(Excludes ad spend where applicable)`;
    
    navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const progress = (step / 6) * 100;

  return (
    <div className={`bg-white rounded-3xl shadow-2xl border border-gray-100 flex flex-col h-full max-h-[90vh] md:max-h-none overflow-hidden w-full`}>
      <div className="bg-brand-primaryDark p-6 text-white relative">
        {onClose && (
          <button onClick={onClose} className="absolute right-4 top-4 text-white/50 hover:text-white">
            <SafeIcon icon={FiX} size={24} />
          </button>
        )}
        <h3 className="text-xl font-bold">Get a Free Estimate</h3>
        <p className="text-brand-blush text-xs font-medium mt-1">Takes 60 seconds. No call required.</p>
        {step < 6 && (
          <div className="mt-4 h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
            <motion.div className="h-full bg-brand-blush" initial={{ width: 0 }} animate={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      <div className="flex-grow overflow-y-auto p-6 md:p-8">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <label className="block text-lg font-bold text-brand-primaryDark mb-4">What type of business do you run?</label>
              {['Dog Walking / Pet Sitting', 'Grooming', 'Daycare / Boarding', 'Pet Professional / Other'].map(type => (
                <button key={type} onClick={() => { setFormData({ ...formData, businessType: type }); nextStep(); }} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${formData.businessType === type ? 'border-brand-mediumPurple bg-brand-mediumPurple/5 text-brand-primaryDark' : 'border-gray-100 hover:border-brand-blush text-gray-600'}`}>
                  {type}
                </button>
              ))}
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
              <label className="block text-lg font-bold text-brand-primaryDark">Where is your business located?</label>
              <input type="text" placeholder="City, State" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-mediumPurple outline-none text-lg" autoFocus />
              <button disabled={!formData.location} onClick={nextStep} className="w-full bg-brand-primaryDark text-white py-4 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                Next <SafeIcon icon={FiArrowRight} />
              </button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <label className="block text-lg font-bold text-brand-primaryDark mb-4">What is your primary goal?</label>
              {['Visibility', 'Leads/Bookings', 'Consistency/Trust', 'Not sure'].map(goal => (
                <button key={goal} onClick={() => { setFormData({ ...formData, goal }); nextStep(); }} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${formData.goal === goal ? 'border-brand-mediumPurple bg-brand-mediumPurple/5 text-brand-primaryDark' : 'border-gray-100 hover:border-brand-blush text-gray-600'}`}>
                  {goal}
                </button>
              ))}
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <label className="block text-lg font-bold text-brand-primaryDark mb-4">Which platforms are you targeting?</label>
              {['Facebook/Instagram', 'TikTok', 'Both'].map(platform => (
                <button key={platform} onClick={() => { setFormData({ ...formData, platforms: platform }); nextStep(); }} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${formData.platforms === platform ? 'border-brand-mediumPurple bg-brand-mediumPurple/5 text-brand-primaryDark' : 'border-gray-100 hover:border-brand-blush text-gray-600'}`}>
                  {platform}
                </button>
              ))}
            </motion.div>
          )}

          {step === 5 && (
            <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
              <div>
                <h4 className="text-xl font-bold text-brand-primaryDark">Optional Add-Ons (Choose any)</h4>
                <p className="text-sm text-gray-500 mt-1">These are extras you can add to your estimate.</p>
              </div>
              <div className="space-y-4">
                {ADD_ONS_DATA.map((addon) => (
                  <div key={addon.id} onClick={() => toggleAddon(addon.id)} className={`p-5 rounded-2xl border-2 transition-all cursor-pointer relative group ${formData[addon.id] ? 'border-brand-mediumPurple bg-brand-mediumPurple/5' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="font-bold text-brand-primaryDark">{addon.name}</h5>
                      <div className={`w-10 h-6 rounded-full transition-colors relative ${formData[addon.id] ? 'bg-brand-mediumPurple' : 'bg-gray-200'}`}>
                        <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${formData[addon.id] ? 'translate-x-4' : ''}`} />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed mb-3">{addon.explainer}</p>
                    {addon.spend && <p className="text-[10px] text-brand-mediumPurple font-bold">{addon.spend}</p>}
                  </div>
                ))}
              </div>
              <button onClick={calculateRecommendation} className="w-full bg-brand-primaryDark text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 mt-4">
                View My Estimate <SafeIcon icon={FiArrowRight} />
              </button>
            </motion.div>
          )}

          {step === 6 && result && (
            <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-8">
              <div className="text-center pb-4 border-b border-gray-100">
                <p className="text-brand-mediumPurple font-bold uppercase tracking-wider text-xs mb-1">Recommended Plan</p>
                <h4 className="text-2xl font-bold text-brand-primaryDark">{result.pkg.name}</h4>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm text-gray-700">
                  <span>Monthly Management Fee</span>
                  <span className="font-bold font-mono">${result.pkg.price}/mo</span>
                </div>
                {result.selectedAddOns.map(addon => (
                  <div key={addon.id} className="flex justify-between items-center text-sm text-gray-700">
                    <span>{addon.name}</span>
                    <span className="font-bold font-mono">{addon.isCustomQuote ? '+ Quote' : `+$${addon.price}/mo`}</span>
                  </div>
                ))}
                <div className="p-5 bg-brand-blush/10 rounded-2xl border border-brand-blush/30 mt-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-[10px] font-black text-brand-primaryDark uppercase tracking-widest mb-1">Est. Total Monthly</p>
                      <p className="text-3xl font-black text-brand-primaryDark">
                        ${result.pkg.price + result.selectedAddOns.reduce((acc, curr) => acc + (curr.price || 0), 0)}
                        <span className="text-sm font-normal text-gray-500"> /mo</span>
                      </p>
                    </div>
                    <button onClick={handleCopy} className="flex items-center gap-2 text-xs font-bold text-brand-mediumPurple hover:text-brand-primaryDark transition-colors px-3 py-2 bg-white rounded-lg border border-brand-mediumPurple/20 shadow-sm">
                      <SafeIcon icon={copied ? FiCheck : FiCopy} /> {copied ? 'Copied!' : 'Copy Summary'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Next Steps Block */}
              <div className="pt-8 border-t border-gray-100 space-y-6">
                <div className="text-center">
                  <h5 className="text-lg font-bold text-brand-primaryDark mb-6">Next Steps</h5>
                  
                  {/* Primary Action */}
                  <div className="space-y-3 mb-8">
                    <a 
                      href="https://tidycal.com/digital-by-perry/schedule-a-call" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-full bg-brand-mediumPurple hover:bg-brand-mauve text-white py-4 px-6 rounded-2xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 group"
                    >
                      <SafeIcon icon={FiCalendar} /> Book a Call for a Custom Quote
                    </a>
                    <p className="text-[11px] text-gray-500 font-medium px-4">
                      Best if you want a tailored plan for your goals, location, and budget.
                    </p>
                  </div>

                  {/* Secondary Action */}
                  <div className="space-y-2">
                    <a 
                      href="https://chatgpt.com/g/g-69a66c0ee14081918a59af31ad9cef4c-pet-content-planner" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-brand-primaryDark font-bold hover:text-brand-mediumPurple transition-colors"
                    >
                      Try the Free Pet Content Planner <SafeIcon icon={FiExternalLink} />
                    </a>
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">
                      Opens in ChatGPT (free account required)
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <button onClick={() => { setStep(1); }} className="text-xs text-gray-400 font-bold hover:text-brand-primaryDark underline decoration-gray-200 underline-offset-4">
                  Start Over
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {step > 1 && step < 6 && (
        <div className="px-6 pb-6">
          <button onClick={prevStep} className="text-gray-400 flex items-center gap-1 text-sm font-medium hover:text-brand-primaryDark">
            <SafeIcon icon={FiArrowLeft} /> Back
          </button>
        </div>
      )}
    </div>
  );
}